import React, { useMemo, useState } from "react";
import { API, Card, FeedBody, Seg, useFeed, useMarkets } from "./ui";
import { BarChart, LineChart } from "./charts";
import { ACTIVE_SCOPE, count, fmtDay, fmtTime, money, pct, toDate, tone, tradePnl } from "./format";

// Window = what the chart, its axis and its headline number all cover.
const WINDOWS = [
  { id: "8h", label: "8h", ms: 8 * 3600e3, bucket: 30 * 60e3 },
  { id: "24h", label: "24h", ms: 24 * 3600e3, bucket: 3600e3 },
  { id: "48h", label: "48h", ms: 48 * 3600e3, bucket: 2 * 3600e3 },
  { id: "7d", label: "7d", ms: 7 * 864e5, bucket: 864e5 },
  { id: "30d", label: "30d", ms: 30 * 864e5, bucket: 864e5 },
];

function EquityCard({ equity }) {
  const { tz } = useMarkets();
  // 24h by default: the 7d total is "This week" and the 30d total is in the header.
  const [win, setWin] = useState("24h");
  const [mode, setMode] = useState("equity");
  const w = WINDOWS.find((x) => x.id === win);
  const { points, buckets, total, n } = useMemo(() => {
    const now = Date.now();
    const start = now - w.ms;
    const rows = equity.map((e) => ({ t: toDate(e.date)?.getTime() || 0, pnl: Number(e.pnl || 0) }))
      .filter((e) => e.t >= start).sort((a, b) => a.t - b.t);
    let cum = 0;
    const pts = [{ t: start, y: 0 }, ...rows.map((r) => ({ t: r.t, y: (cum += r.pnl) }))];
    pts.push({ t: now, y: cum });
    const map = new Map();
    for (let t = Math.floor(start / w.bucket) * w.bucket; t <= now; t += w.bucket) map.set(t, 0);
    rows.forEach((r) => { const k = Math.floor(r.t / w.bucket) * w.bucket; map.set(k, (map.get(k) || 0) + r.pnl); });
    return { points: pts, buckets: [...map.entries()].map(([t, pnl]) => ({ t, pnl })), total: cum, n: rows.length };
  }, [equity, w]);
  const fmtX = (t) => (w.ms <= 48 * 3600e3 ? fmtTime(t, tz).replace(/ [A-Z]{2,4}$/, "") : new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "short", day: "numeric" }).format(t));

  return (
    <Card className="mk-span-8" title={mode === "equity" ? "Cumulative equity" : "P&L by period"}
      scope={`Last ${w.label} · ${ACTIVE_SCOPE} · realized P&L · times ${fmtTime(new Date(), tz).split(" ").pop()}`}
      actions={(
        <>
          <Seg label="Chart type" value={mode} onChange={setMode} options={[{ value: "equity", label: "Equity" }, { value: "pnl", label: "By period" }]} />
          <Seg label="Window" value={win} onChange={setWin} options={WINDOWS.map((x) => ({ value: x.id, label: x.label }))} />
        </>
      )}>
      <div className="mk-headline-num">
        <strong className={`mk-num ${tone(total)}`}>{money(total)}</strong>
        <span>{count(n)} closed trades in the last {w.label}</span>
      </div>
      {mode === "equity"
        ? <LineChart points={points} height={250} fmtX={fmtX} ariaLabel={`Cumulative realized P&L, last ${w.label}`} />
        : <BarChart buckets={buckets} height={250} fmtX={fmtX} ariaLabel={`Realized P&L per period, last ${w.label}`} />}
    </Card>
  );
}

function ThisWeek() {
  const feed = useFeed(`${API}/analytics/weekly-summary?scope=active`, { interval: 60000 });
  return (
    <Card title="This week" scope={`Rolling last 7 days vs the 7 before · ${ACTIVE_SCOPE}`}>
      <FeedBody feed={feed} rows={2}>
        {(d) => {
          const tw = d.this_week || {}; const del = d.delta || {};
          return (
            <div className="mk-stat-row is-3">
              <div><span className="mk-label">Realized P&amp;L</span><strong className={`mk-num mk-big ${tone(tw.pnl)}`}>{money(tw.pnl)}</strong>
                <span className={`mk-sub ${tone(del.pnl)}`}>{money(del.pnl)} vs prior</span></div>
              <div><span className="mk-label">Win rate</span><strong className="mk-num mk-big">{pct(tw.win_rate)}</strong>
                <span className={`mk-sub ${tone(del.win_rate)}`}>{del.win_rate >= 0 ? "+" : ""}{Number(del.win_rate || 0).toFixed(1)} pts</span></div>
              <div><span className="mk-label">Trades</span><strong className="mk-num mk-big">{count(tw.trades)}</strong>
                <span className={`mk-sub ${tone(del.trades)}`}>{del.trades >= 0 ? "+" : ""}{del.trades} vs prior</span></div>
            </div>
          );
        }}
      </FeedBody>
    </Card>
  );
}

const REASONS = [
  ["take_profit", "Take profit", "#4ade80"], ["trailing_stop", "Trailing stop", "#4ad6ff"],
  ["stop_loss", "Stop loss", "#ff5d6c"], ["liquidation", "Liquidation", "#ff2d55"], ["other", "Other", "#8b9488"],
];

function CloseReasons({ trades }) {
  const rows = useMemo(() => {
    const m = Object.fromEntries(REASONS.map(([k]) => [k, { n: 0, pnl: 0 }]));
    trades.forEach((t) => {
      const r = String(t.close_reason || t.exit_reason || "").toLowerCase().replace(/ /g, "_");
      const k = m[r] ? r : "other";
      m[k].n += 1; m[k].pnl += tradePnl(t);
    });
    const total = trades.length || 1;
    return REASONS.map(([k, label, c]) => ({ k, label, c, ...m[k], share: (m[k].n / total) * 100 })).filter((r) => r.n);
  }, [trades]);
  return (
    <Card title="Close reasons" scope={`All closed trades · ${ACTIVE_SCOPE}`}>
      <ul className="mk-bars">
        {rows.map((r) => (
          <li key={r.k}>
            <span>{r.label}</span>
            <span className="mk-meter-track"><i style={{ width: `${r.share}%`, background: r.c }} /></span>
            <span className="mk-num">{pct(r.share, { dec: 0 })}</span>
            <span className={`mk-num ${tone(r.pnl)}`}>{money(r.pnl)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Leaderboard() {
  const [days, setDays] = useState(30);
  const [all, setAll] = useState(false);
  const feed = useFeed(`${API}/analytics/engine-leaderboard?days=${days}&scope=active`);
  return (
    <Card className="mk-span-6 mk-fixed-360" title="Engine leaderboard"
      scope={`Trades opened in the last ${days} days · ${ACTIVE_SCOPE} · win = closed at take-profit or trailing stop`}
      actions={<Seg label="Lookback" value={days} onChange={setDays} options={[7, 30, 90].map((n) => ({ value: n, label: `${n}d` }))} />}
      bodyClass="mk-scroll">
      <FeedBody feed={feed} rows={6}>
        {(d) => {
          const list = d.engines || [];
          if (!list.length) return <p className="mk-empty">No engine closed a trade in this window.</p>;
          const shown = all ? list : list.slice(0, 8);
          return (
            <>
              <table className="mk-table">
                <thead><tr><th className="r">#</th><th>Engine</th><th className="r">P&amp;L</th><th className="r">Win rate</th><th className="r">Trades</th></tr></thead>
                <tbody>
                  {shown.map((e, i) => (
                    <tr key={e.engine}>
                      <td className="r mk-num">{i + 1}</td>
                      <td className="mk-trunc" title={e.engine}>{e.engine.replace(/_/g, " ")}</td>
                      <td className={`r mk-num ${tone(e.pnl)}`}>{money(e.pnl)}</td>
                      <td className="r mk-num">{pct(e.win_rate)}</td>
                      <td className="r mk-num">{count(e.trades)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {list.length > 8 ? (
                <button type="button" className="mk-link" onClick={() => setAll((v) => !v)}>
                  {all ? "Show top 8" : `Show all ${list.length}`}
                </button>
              ) : null}
            </>
          );
        }}
      </FeedBody>
    </Card>
  );
}

function SessionLedger() {
  const { tz } = useMarkets();
  const [recent, setRecent] = useState(false);
  const feed = useFeed(`${API}/analytics/daily-pnl?scope=active&tz=${encodeURIComponent(tz)}${recent ? "&days=30" : ""}`);
  const tzl = fmtTime(new Date(), tz).split(" ").pop();
  return (
    <Card className="mk-span-6 mk-fixed-360" title="Session ledger"
      scope={`${recent ? "Last 30 days" : "All time"} · ${ACTIVE_SCOPE} · days in ${tzl}`}
      actions={<Seg label="Ledger window" value={recent} onChange={setRecent} options={[{ value: false, label: "All time" }, { value: true, label: "30 days" }]} />}
      bodyClass="mk-scroll">
      <FeedBody feed={feed} rows={7}>
        {(d) => {
          const rows = (d.days || []).slice().reverse();
          return (
            <>
              <div className="mk-total-line">
                <span>Accumulated realized P&amp;L</span>
                <strong className={`mk-num ${tone(d.total_pnl)}`}>{money(d.total_pnl)}</strong>
                <span className="mk-sub">{count(d.total_trades)} trades · {count(d.session_count)} days</span>
              </div>
              <table className="mk-table">
                <thead><tr><th>Day ({tzl})</th><th className="r">Trades</th><th className="r">Day P&amp;L</th><th className="r">Accumulated</th></tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.date}>
                      <td className="mk-num">{fmtDay(r.date)}</td>
                      <td className="r mk-num">{r.trades}</td>
                      <td className={`r mk-num ${tone(r.day_pnl)}`}>{money(r.day_pnl)}</td>
                      <td className={`r mk-num ${tone(r.cumulative_pnl)}`}>{money(r.cumulative_pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          );
        }}
      </FeedBody>
    </Card>
  );
}

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "a" : "p"}`;

function Heatmap() {
  const { tz } = useMarkets();
  const [days, setDays] = useState(30);
  const feed = useFeed(`${API}/analytics/pnl-heatmap?days=${days}&scope=active&tz=${encodeURIComponent(tz)}`);
  const tzl = fmtTime(new Date(), tz).split(" ").pop();
  return (
    <Card className="mk-span-7" title="P&L by weekday and hour"
      scope={`Trades opened in the last ${days} days · ${ACTIVE_SCOPE} · hours in ${tzl}`}
      actions={<Seg label="Lookback" value={days} onChange={setDays} options={[30, 60, 90].map((n) => ({ value: n, label: `${n}d` }))} />}>
      <FeedBody feed={feed} rows={7} height={220}>
        {(d) => {
          const grid = d.grid || [];
          const max = Math.max(1, ...grid.flat().map((c) => Math.abs(c.pnl)));
          const cellBg = (c) => {
            if (!c.n) return "transparent";
            const a = 0.15 + 0.75 * Math.min(1, Math.sqrt(Math.abs(c.pnl) / max));
            return c.pnl >= 0 ? `rgba(74,222,128,${a})` : `rgba(255,93,108,${a})`;
          };
          const win = (c) => `${DOW[c.dow]} ${hourLabel(c.hour)}`;
          return (
            <div className="mk-heat-wrap">
              <div className="mk-heatmap" role="table" aria-label="Realized P&L by weekday and hour">
                <div className="mk-hm-row is-head" role="row">
                  <span />
                  {Array.from({ length: 24 }, (_, h) => <span key={h} className="mk-hm-h">{h % 3 === 0 ? hourLabel(h) : ""}</span>)}
                </div>
                {grid.map((row, di) => (
                  <div key={di} className="mk-hm-row" role="row">
                    <span className="mk-hm-d">{DOW[di]}</span>
                    {row.map((c, h) => (
                      <span key={h} className="mk-hm-cell" style={{ background: cellBg(c) }}
                        title={c.n ? `${DOW[di]} ${hourLabel(h)}: ${money(c.pnl)} · ${c.n} trades · ${Math.round((c.wins / c.n) * 100)}% wins` : `${DOW[di]} ${hourLabel(h)}: no trades`} />
                    ))}
                  </div>
                ))}
              </div>
              <div className="mk-hm-lists">
                <div>
                  <h4>Best windows</h4>
                  <ol>{(d.best_cells || []).slice(0, 4).map((c) => <li key={`b${c.dow}${c.hour}`}><span>{win(c)}</span><b className={`mk-num ${tone(c.pnl)}`}>{money(c.pnl)}</b></li>)}</ol>
                </div>
                <div>
                  <h4>Worst windows</h4>
                  <ol>{(d.worst_cells || []).slice(0, 4).map((c) => <li key={`w${c.dow}${c.hour}`}><span>{win(c)}</span><b className={`mk-num ${tone(c.pnl)}`}>{money(c.pnl)}</b></li>)}</ol>
                </div>
              </div>
            </div>
          );
        }}
      </FeedBody>
    </Card>
  );
}

const DIST = [
  { label: "below -20%", min: -Infinity, max: -20, cls: "is-down" }, { label: "-20 to -10%", min: -20, max: -10, cls: "is-down" },
  { label: "-10 to 0%", min: -10, max: 0, cls: "is-down" }, { label: "0 to +10%", min: 0, max: 10, cls: "is-up" },
  { label: "+10 to +20%", min: 10, max: 20, cls: "is-up" }, { label: "above +20%", min: 20, max: Infinity, cls: "is-up" },
];

function Distribution({ trades }) {
  const rows = useMemo(() => DIST.map((b) => ({ ...b, n: trades.filter((t) => { const p = Number(t.pnl_pct ?? 0); return p >= b.min && p < b.max; }).length })), [trades]);
  const max = Math.max(1, ...rows.map((r) => r.n));
  const stats = useMemo(() => {
    const v = trades.map((t) => Number(t.pnl_pct ?? 0)).filter(Number.isFinite).sort((a, b) => a - b);
    if (!v.length) return null;
    const mid = v.length >> 1;
    return { median: v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2, mean: v.reduce((a, b) => a + b, 0) / v.length,
      positive: (v.filter((x) => x > 0).length / v.length) * 100 };
  }, [trades]);
  return (
    <Card className="mk-span-5" title="Return distribution" scope={`Return on margin per closed trade · ${ACTIVE_SCOPE} · ${count(trades.length)} trades`}>
      <ul className="mk-hbars is-fill">
        {rows.map((r) => (
          <li key={r.label}>
            <span>{r.label}</span>
            <span className="mk-meter-track"><i className={r.cls === "is-up" ? "is-up-fill" : "is-down-fill"} style={{ width: `${(r.n / max) * 100}%` }} /></span>
            <span className="mk-num">{count(r.n)}</span>
          </li>
        ))}
      </ul>
      {stats ? (
        <div className="mk-stat-row is-3 mk-foot-stats">
          <div><span className="mk-label">Median return</span><strong className={`mk-num ${tone(stats.median)}`}>{pct(stats.median, { sign: true })}</strong></div>
          <div><span className="mk-label">Mean return</span><strong className={`mk-num ${tone(stats.mean)}`}>{pct(stats.mean, { sign: true })}</strong></div>
          <div><span className="mk-label">Trades in profit</span><strong className="mk-num">{pct(stats.positive)}</strong></div>
        </div>
      ) : null}
    </Card>
  );
}

const CONF = [{ label: "Under 70%", min: 0, max: 70 }, { label: "70–80%", min: 70, max: 80 }, { label: "80–90%", min: 80, max: 90 }, { label: "90% and up", min: 90, max: 101 }];

function ConfidenceCard({ trades }) {
  const rows = useMemo(() => CONF.map((b) => {
    const list = trades.filter((t) => { const c = Number(t.signal_data?.confidence ?? t.confidence ?? NaN); return c >= b.min && c < b.max; });
    const wins = list.filter((t) => tradePnl(t) > 0).length;
    const graded = list.filter((t) => tradePnl(t) !== 0).length;
    const total = list.reduce((a, t) => a + tradePnl(t), 0);
    return { ...b, n: list.length, wr: graded ? (wins / graded) * 100 : null, total, avg: list.length ? total / list.length : 0 };
  }).filter((r) => r.n), [trades]);

  // Caption from the data, not an assertion.
  const caption = useMemo(() => {
    const g = rows.filter((r) => r.wr != null && r.n >= 20);
    if (g.length < 2) return "Too few graded trades per bucket to compare.";
    const rising = g.every((r, i) => i === 0 || r.wr >= g[i - 1].wr);
    const falling = g.every((r, i) => i === 0 || r.wr <= g[i - 1].wr);
    const hi = g.reduce((a, b) => (b.wr > a.wr ? b : a));
    const lo = g.reduce((a, b) => (b.wr < a.wr ? b : a));
    if (rising) return `Win rate rises with confidence, from ${pct(g[0].wr)} to ${pct(g[g.length - 1].wr)}.`;
    if (falling) return `Win rate falls as confidence rises, from ${pct(g[0].wr)} to ${pct(g[g.length - 1].wr)}.`;
    return `No consistent link: highest win rate at ${hi.label} (${pct(hi.wr)}), lowest at ${lo.label} (${pct(lo.wr)}).`;
  }, [rows]);

  return (
    <Card className="mk-span-6 mk-fixed-row4" title="Confidence vs win rate" scope={`Signal confidence at entry · ${ACTIVE_SCOPE} · buckets under 20 trades are not compared`}>
      <ul className="mk-conf is-fill">
        {rows.map((r) => (
          <li key={r.label}>
            <div className="mk-conf-head">
              <span>{r.label}</span>
              <span className="mk-sub">{count(r.n)} trades · avg {money(r.avg)} · total <b className={tone(r.total)}>{money(r.total)}</b></span>
              <b className="mk-num">{r.wr == null ? "—" : pct(r.wr)}</b>
            </div>
            <span className="mk-meter-track"><i style={{ width: `${r.wr ?? 0}%` }} className={r.wr >= 50 ? "is-up-fill" : "is-warn-fill"} /></span>
          </li>
        ))}
      </ul>
      <p className="mk-caption">{caption}</p>
    </Card>
  );
}

function Scorecard() {
  const feed = useFeed(`${API}/analytics/signal-scorecard`, { interval: 120000, timeoutMs: 45000 });
  return (
    <Card className="mk-span-6 mk-fixed-row4" title="Signal scorecard" bodyClass="mk-scroll"
      scope={feed.data ? `${count(feed.data.setup_count)} broadcast setups · last ${feed.data.window_days} days · if every signal were taken` : "Broadcast setups backtested against real price"}>
      <FeedBody feed={feed} rows={6}>
        {(d) => {
          const h = d.headline || {};
          const ded = d.deduped || {};
          return (
            <>
              <div className="mk-stat-row is-3">
                <div><span className="mk-label">Edge available</span><strong className={`mk-num mk-big ${tone(h.edge_available_usd)}`}>{money(h.edge_available_usd)}</strong><span className="mk-sub">all signals followed</span></div>
                <div><span className="mk-label">Filter kept</span><strong className={`mk-num mk-big ${tone(h.edge_captured_usd)}`}>{money(h.edge_captured_usd)}</strong><span className="mk-sub">passed the gate</span></div>
                <div><span className="mk-label">Left on table</span><strong className="mk-num mk-big is-down">{money(-Math.abs(h.edge_left_on_table_usd || 0))}</strong><span className="mk-sub">blocked by filters</span></div>
              </div>
              <div className="mk-total-line">
                <span>AEON actually realized, same window</span>
                <strong className={`mk-num ${tone(d.actual_realized?.pnl)}`}>{money(d.actual_realized?.pnl)}</strong>
                <span className="mk-sub">{pct(d.actual_realized?.win_rate)} win rate</span>
              </div>
              <dl className="mk-kv">
                <div><dt>Short signals</dt><dd className={`mk-num ${tone(ded.short_only?.usd)}`}>{money(ded.short_only?.usd)} <span>{pct(ded.short_only?.win_rate)} win</span></dd></div>
                <div><dt>Long signals</dt><dd className={`mk-num ${tone(ded.long_only?.usd)}`}>{money(ded.long_only?.usd)} <span>{pct(ded.long_only?.win_rate)} win</span></dd></div>
              </dl>
              {(d.by_week || []).length ? (
                <table className="mk-table">
                  <thead><tr><th>Week</th><th>Bias</th><th className="r">Win rate</th><th className="r">R</th></tr></thead>
                  <tbody>
                    {d.by_week.map((w) => (
                      <tr key={w.week}>
                        <td className="mk-num">{String(w.week).slice(5)}</td>
                        <td className={w.bias === "LONG" ? "is-up" : w.bias === "SHORT" ? "is-down" : ""}>{w.bias}</td>
                        <td className="r mk-num">{pct(w.win_rate)}</td>
                        <td className={`r mk-num ${tone(w.r_sum)}`}>{w.r_sum >= 0 ? "+" : ""}{w.r_sum}R</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
              <p className="mk-caption">One position per symbol, real price, ${d.risk_per_trade} risk per trade, no fees or slippage, same-bar hits counted as losses.</p>
            </>
          );
        }}
      </FeedBody>
    </Card>
  );
}

export default function Performance({ equity, activeTrades }) {
  return (
    <div className="mk-grid-12">
      <EquityCard equity={equity} />
      <div className="mk-span-4 mk-stack">
        <ThisWeek />
        <CloseReasons trades={activeTrades} />
      </div>
      <Leaderboard />
      <SessionLedger />
      <Heatmap />
      <Distribution trades={activeTrades} />
      <ConfidenceCard trades={activeTrades} />
      <Scorecard />
    </div>
  );
}
